import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  People,
  Assignment,
  Timeline,
  Speed,
  BugReport,
  Assessment,
  Refresh,
  Download,
  FilterList,
  Search,
  DateRange
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter
} from 'recharts';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { useLeadershipWebSocket } from '../hooks/useLeadershipWebSocket';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`leadership-tabpanel-${index}`}
      aria-labelledby={`leadership-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const LeadershipDashboard: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [userPerformance, setUserPerformance] = useState<any[]>([]);
  const [sprintPerformance, setSprintPerformance] = useState<any[]>([]);
  const [projectPerformance, setProjectPerformance] = useState<any[]>([]);
  const [riskAssessment, setRiskAssessment] = useState<any>(null);
  
  // Filters
  const [dateRange, setDateRange] = useState({ start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), end: new Date() });
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedMetric, setSelectedMetric] = useState<string>('all');
  const [datePreset, setDatePreset] = useState<string>('last30days');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedUserRole, setSelectedUserRole] = useState<string>('');

  // Real-time updates
  const {
    isConnected,
    lastUpdate,
    connectionError,
    subscribeToDashboard,
    refreshDashboard
  } = useLeadershipWebSocket();

  // Load saved preferences
  useEffect(() => {
    const savedFilters = localStorage.getItem('leadershipDashboardFilters');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        if (filters.datePreset) setDatePreset(filters.datePreset);
        if (filters.selectedProject) setSelectedProject(filters.selectedProject);
        if (filters.selectedMetric) setSelectedMetric(filters.selectedMetric);
        if (filters.selectedUserRole) setSelectedUserRole(filters.selectedUserRole);
        if (filters.selectedTeam) setSelectedTeam(filters.selectedTeam);
      } catch (error) {
        console.error('Error loading saved filters:', error);
      }
    }
  }, []);

  // Save preferences when filters change
  useEffect(() => {
    const filters = {
      datePreset,
      selectedProject,
      selectedMetric,
      selectedUserRole,
      selectedTeam
    };
    localStorage.setItem('leadershipDashboardFilters', JSON.stringify(filters));
  }, [datePreset, selectedProject, selectedMetric, selectedUserRole, selectedTeam]);

  useEffect(() => {
    loadDashboardData();
  }, [dateRange, selectedProject, selectedMetric]);

  // Real-time updates
  useEffect(() => {
    if (isConnected) {
      const unsubscribe = subscribeToDashboard((data) => {
        console.log('📊 Real-time dashboard update received:', data);
        if (data.type === 'dashboard') {
          setDashboardData(data.data);
        }
      });

      return unsubscribe;
    }
  }, [isConnected, subscribeToDashboard]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        dashboard,
        users,
        sprints,
        projects,
        risks
      ] = await Promise.all([
        apiService.getLeadershipDashboard(),
        apiService.getUsersPerformanceSummary(dateRange),
        apiService.getSprintsPerformanceSummary(selectedProject),
        apiService.getProjectsPerformanceSummary(),
        apiService.getRiskAssessment()
      ]);

      setDashboardData(dashboard);
      setUserPerformance(users);
      setSprintPerformance(sprints);
      setProjectPerformance(projects);
      setRiskAssessment(risks);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset);
    const now = new Date();
    let start = new Date();
    
    switch (preset) {
      case 'last7days':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'last30days':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'last90days':
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case 'lastQuarter':
        start = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 - 3, 1);
        break;
      case 'lastYear':
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      case 'custom':
        // Don't change dates for custom
        return;
      default:
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    
    setDateRange({ start, end: now });
  };

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      default: return 'success';
    }
  };

  // Chart data preparation
  const prepareVelocityChartData = () => {
    return sprintPerformance.map(sprint => ({
      name: sprint.sprintName,
      planned: sprint.plannedStoryPoints,
      completed: sprint.completedStoryPoints,
      velocity: sprint.velocity
    }));
  };

  const prepareUserAllocationData = () => {
    // Filter by role if selected
    let filteredUsers = userPerformance;
    if (selectedUserRole) {
      filteredUsers = userPerformance.filter(user => user.userRole === selectedUserRole);
    }
    
    return filteredUsers.map(user => ({
      name: user.userName,
      allocation: user.totalAllocation,
      velocity: user.velocity,
      quality: user.qualityScore
    }));
  };

  const prepareProjectHealthData = () => {
    return projectPerformance.map(project => ({
      name: project.projectName,
      health: project.healthScore,
      completion: project.completionRate,
      velocity: project.averageVelocity
    }));
  };

  const exportToCSV = () => {
    let csvData: any[] = [];
    let fileName = 'leadership-dashboard';

    switch (activeTab) {
      case 0: // Executive Summary
        csvData = sprintPerformance.map(sprint => ({
          'Sprint Name': sprint.sprintName,
          'Project': sprint.projectName,
          'Planned Story Points': sprint.plannedStoryPoints,
          'Completed Story Points': sprint.completedStoryPoints,
          'Velocity': sprint.velocity,
          'Completion Rate': sprint.completionRate + '%',
          'Status': sprint.status
        }));
        fileName = 'executive-summary';
        break;
      
      case 1: // User Performance
        csvData = userPerformance.map(user => ({
          'User Name': user.userName,
          'Role': user.userRole,
          'Velocity': user.velocity,
          'Quality Score': user.qualityScore + '%',
          'Allocation': user.totalAllocation + '%',
          'Issues Completed': user.issuesCompleted,
          'Story Points Completed': user.storyPointsCompleted,
          'On-Time Delivery': user.onTimeDelivery + '%'
        }));
        fileName = 'user-performance';
        break;
      
      case 2: // Sprint Analytics
        csvData = sprintPerformance.map(sprint => ({
          'Sprint': sprint.sprintName,
          'Project': sprint.projectName,
          'Start Date': new Date(sprint.startDate).toLocaleDateString(),
          'End Date': new Date(sprint.endDate).toLocaleDateString(),
          'Status': sprint.status,
          'Planned SP': sprint.plannedStoryPoints,
          'Completed SP': sprint.completedStoryPoints,
          'Velocity': sprint.velocity,
          'Risk Level': sprint.riskLevel
        }));
        fileName = 'sprint-analytics';
        break;
      
      case 3: // Project Health
        csvData = projectPerformance.map(project => ({
          'Project': project.projectName,
          'Key': project.projectKey,
          'Status': project.status,
          'Health Score': project.healthScore,
          'Completion Rate': project.completionRate + '%',
          'Average Velocity': project.averageVelocity,
          'Total Issues': project.totalIssues,
          'Completed Issues': project.completedIssues,
          'At Risk': project.isAtRisk ? 'Yes' : 'No'
        }));
        fileName = 'project-health';
        break;
      
      case 4: // Risk Assessment
        csvData = riskAssessment?.riskFactors?.map((risk: any) => ({
          'Risk Name': risk.name,
          'Category': risk.category,
          'Severity': risk.severity,
          'Impact': risk.impact,
          'Probability': risk.probability,
          'Score': risk.score,
          'Description': risk.description,
          'Affected Entity': risk.affectedEntity.type + ' - ' + risk.affectedEntity.name,
          'Mitigation Actions': risk.mitigationActions.join('; ')
        })) || [];
        fileName = 'risk-assessment';
        break;
    }

    if (csvData.length === 0) {
      alert('No data to export');
      return;
    }

    // Convert to CSV
    const headers = Object.keys(csvData[0]);
    const csvRows = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => {
        const value = row[header]?.toString() || '';
        // Escape commas and quotes
        return value.includes(',') || value.includes('"') ? `"${value.replace(/"/g, '""')}"` : value;
      }).join(','))
    ];
    const csvString = csvRows.join('\n');

    // Download file
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            Leadership Dashboard
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Real-time status indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: isConnected ? 'success.main' : 'error.main',
                  animation: isConnected ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 }
                  }
                }}
              />
              <Typography variant="caption" color="textSecondary">
                {isConnected ? 'Live Updates' : 'Offline'}
              </Typography>
              {lastUpdate && (
                <Typography variant="caption" color="textSecondary">
                  Last update: {lastUpdate.toLocaleTimeString()}
                </Typography>
              )}
            </Box>
            
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                loadDashboardData();
                refreshDashboard();
              }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={exportToCSV}
            >
              Export CSV
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={datePreset}
                  onChange={(e) => handleDatePresetChange(e.target.value)}
                  label="Date Range"
                >
                  <MenuItem value="last7days">Last 7 Days</MenuItem>
                  <MenuItem value="last30days">Last 30 Days</MenuItem>
                  <MenuItem value="last90days">Last 90 Days</MenuItem>
                  <MenuItem value="lastQuarter">Last Quarter</MenuItem>
                  <MenuItem value="lastYear">Last Year</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={dateRange.start.toISOString().split('T')[0]}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, start: new Date(e.target.value) }));
                  setDatePreset('custom');
                }}
                InputLabelProps={{ shrink: true }}
                disabled={datePreset !== 'custom'}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                value={dateRange.end.toISOString().split('T')[0]}
                onChange={(e) => {
                  setDateRange(prev => ({ ...prev, end: new Date(e.target.value) }));
                  setDatePreset('custom');
                }}
                InputLabelProps={{ shrink: true }}
                disabled={datePreset !== 'custom'}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Project</InputLabel>
                <Select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  label="Project"
                >
                  <MenuItem value="">All Projects</MenuItem>
                  {projectPerformance.map(project => (
                    <MenuItem key={project.projectId} value={project.projectId}>
                      {project.projectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Metric</InputLabel>
                <Select
                  value={selectedMetric}
                  onChange={(e) => setSelectedMetric(e.target.value)}
                  label="Metric"
                >
                  <MenuItem value="all">All Metrics</MenuItem>
                  <MenuItem value="velocity">Velocity</MenuItem>
                  <MenuItem value="quality">Quality</MenuItem>
                  <MenuItem value="delivery">Delivery</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>User Role</InputLabel>
                <Select
                  value={selectedUserRole}
                  onChange={(e) => setSelectedUserRole(e.target.value)}
                  label="User Role"
                >
                  <MenuItem value="">All Roles</MenuItem>
                  <MenuItem value="root_user">Root User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="super_manager">Super Manager</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                  <MenuItem value="hr_admin">HR Admin</MenuItem>
                  <MenuItem value="employee">Employee</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Connection Error Alert */}
        {connectionError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Real-time updates unavailable: {connectionError}
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Total number of projects in the organization. Active projects are currently being worked on." arrow>
              <Card sx={{ cursor: 'help' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="h6">
                        Total Projects
                      </Typography>
                      <Typography variant="h4">
                        {dashboardData?.summary.totalProjects || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {dashboardData?.summary.activeProjects || 0} Active
                      </Typography>
                    </Box>
                    <Assignment color="primary" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Total team members across all projects. Active members are currently working and assigned to tasks." arrow>
              <Card sx={{ cursor: 'help' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="h6">
                        Team Members
                      </Typography>
                      <Typography variant="h4">
                        {dashboardData?.summary.totalUsers || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {dashboardData?.summary.activeUsers || 0} Active
                      </Typography>
                    </Box>
                    <People color="secondary" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Number of sprints currently in progress. Total includes all sprints (planned, active, and completed)." arrow>
              <Card sx={{ cursor: 'help' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="h6">
                        Active Sprints
                      </Typography>
                      <Typography variant="h4">
                        {dashboardData?.summary.activeSprints || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {dashboardData?.summary.totalSprints || 0} Total
                      </Typography>
                    </Box>
                    <Timeline color="success" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Tooltip title="Overall organizational health score (0-100). Based on project completion rates, sprint velocity, and quality metrics. Green (80+) = Excellent, Yellow (60-79) = Good, Red (<60) = Needs Attention." arrow>
              <Card sx={{ cursor: 'help' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="h6">
                        Overall Health
                      </Typography>
                      <Typography variant="h4">
                        {Math.round(dashboardData?.summary.overallHealthScore || 0)}%
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={dashboardData?.summary.overallHealthScore || 0}
                          color={getHealthColor(dashboardData?.summary.overallHealthScore || 0)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    </Box>
                    <Assessment color="info" sx={{ fontSize: 40 }} />
                  </Box>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="leadership dashboard tabs">
            <Tab label="Executive Summary" />
            <Tab label="User Performance" />
            <Tab label="Sprint Analytics" />
            <Tab label="Project Health" />
            <Tab label="Risk Assessment" />
          </Tabs>

          {/* Executive Summary Tab */}
          <TabPanel value={activeTab} index={0}>
            <Grid container spacing={3}>
              {/* Velocity Trend Chart */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Sprint Velocity Trends
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={prepareVelocityChartData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="planned" stroke="#8884d8" name="Planned SP" />
                        <Line type="monotone" dataKey="completed" stroke="#82ca9d" name="Completed SP" />
                        <Line type="monotone" dataKey="velocity" stroke="#ffc658" name="Velocity" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Risk Alerts */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Risk Alerts
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning color="error" />
                        <Typography variant="body2">
                          {dashboardData?.riskAlerts.overdueProjects || 0} Overdue Projects
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning color="warning" />
                        <Typography variant="body2">
                          {dashboardData?.riskAlerts.atRiskSprints || 0} At-Risk Sprints
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BugReport color="error" />
                        <Typography variant="body2">
                          {dashboardData?.riskAlerts.qualityIssues || 0} Quality Issues
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* User Performance Tab */}
          <TabPanel value={activeTab} index={1}>
            <Grid container spacing={3}>
              {/* User Allocation Chart */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      User Allocation Distribution
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={prepareUserAllocationData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip />
                        <Bar dataKey="allocation" fill="#8884d8" name="Allocation %" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* User Performance Table */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Top Performers
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {userPerformance.slice(0, 10).map((user, index) => (
                        <Box key={user.userId} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="subtitle2">{user.userName}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {user.userRole}
                              </Typography>
                            </Box>
                            <Chip
                              label={`${Math.round(user.qualityScore)}%`}
                              color={getHealthColor(user.qualityScore)}
                              size="small"
                            />
                          </Box>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              Velocity: {Math.round(user.velocity)} SP/sprint
                            </Typography>
                            <Typography variant="body2">
                              Allocation: {Math.round(user.totalAllocation)}%
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Sprint Analytics Tab */}
          <TabPanel value={activeTab} index={2}>
            <Grid container spacing={3}>
              {/* Sprint Performance Chart */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Sprint Performance Overview
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <ScatterChart data={sprintPerformance}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="plannedStoryPoints" name="Planned SP" />
                        <YAxis dataKey="completedStoryPoints" name="Completed SP" />
                        <RechartsTooltip cursor={{ strokeDasharray: '3 3' }} />
                        <Scatter dataKey="velocity" fill="#8884d8" />
                      </ScatterChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Project Health Tab */}
          <TabPanel value={activeTab} index={3}>
            <Grid container spacing={3}>
              {/* Project Health Chart */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Project Health vs Velocity
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={prepareProjectHealthData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip />
                        <Area type="monotone" dataKey="health" stackId="1" stroke="#8884d8" fill="#8884d8" />
                        <Area type="monotone" dataKey="velocity" stackId="2" stroke="#82ca9d" fill="#82ca9d" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Project Health Summary */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Project Health Summary
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {projectPerformance.map(project => (
                        <Box key={project.projectId} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="subtitle2">{project.projectName}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={project.healthScore}
                              color={getHealthColor(project.healthScore)}
                              sx={{ flexGrow: 1, height: 6 }}
                            />
                            <Typography variant="caption">
                              {Math.round(project.healthScore)}%
                            </Typography>
                          </Box>
                          {project.isAtRisk && (
                            <Chip
                              label="At Risk"
                              color="error"
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Risk Assessment Tab */}
          <TabPanel value={activeTab} index={4}>
            <Grid container spacing={3}>
              {/* Overall Risk Summary */}
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Overall Risk Level
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 3 }}>
                      <Chip
                        label={riskAssessment?.overallRiskLevel?.toUpperCase() || 'CALCULATING...'}
                        color={
                          riskAssessment?.overallRiskLevel === 'critical' ? 'error' :
                          riskAssessment?.overallRiskLevel === 'high' ? 'warning' :
                          riskAssessment?.overallRiskLevel === 'medium' ? 'info' : 'success'
                        }
                        sx={{ fontSize: '1.2rem', py: 3, px: 2 }}
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary" align="center">
                      Total Risk Score: {riskAssessment?.totalRiskScore?.toFixed(0) || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                      {riskAssessment?.riskFactors?.length || 0} Risk Factors Identified
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Critical Alerts */}
              <Grid item xs={12} md={8}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Critical Alerts ({riskAssessment?.criticalAlerts || 0})
                    </Typography>
                    {riskAssessment?.criticalAlerts > 0 ? (
                      <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                        {riskAssessment?.riskFactors?.filter((r: any) => r.severity === 'critical').map((risk: any) => (
                          <Alert key={risk.id} severity="error" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">{risk.name}</Typography>
                            <Typography variant="body2">{risk.description}</Typography>
                          </Alert>
                        ))}
                      </Box>
                    ) : (
                      <Alert severity="success">
                        No critical risks detected
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Recommendations */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recommendations
                    </Typography>
                    <Box>
                      {riskAssessment?.recommendations?.map((rec: string, index: number) => (
                        <Box key={index} sx={{ mb: 1, display: 'flex', alignItems: 'flex-start' }}>
                          <Typography variant="body2">{rec}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Risk Matrix */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Risk Distribution
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'High Risk', value: riskAssessment?.riskMatrix?.high?.length || 0, fill: '#f44336' },
                            { name: 'Medium Risk', value: riskAssessment?.riskMatrix?.medium?.length || 0, fill: '#ff9800' },
                            { name: 'Low Risk', value: riskAssessment?.riskMatrix?.low?.length || 0, fill: '#4caf50' }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: ${entry.value}`}
                          outerRadius={80}
                          dataKey="value"
                        >
                          {[0, 1, 2].map((index) => (
                            <Cell key={`cell-${index}`} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Risk Trends */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Risk Trends (Last 7 Weeks)
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={riskAssessment?.riskTrends || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Line type="monotone" dataKey="highRiskCount" stroke="#f44336" name="High Risk" />
                        <Line type="monotone" dataKey="mediumRiskCount" stroke="#ff9800" name="Medium Risk" />
                        <Line type="monotone" dataKey="lowRiskCount" stroke="#4caf50" name="Low Risk" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Risk Factors by Category */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Risk Factors by Category
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={
                        Object.entries(
                          riskAssessment?.riskFactors?.reduce((acc: any, risk: any) => {
                            acc[risk.category] = (acc[risk.category] || 0) + 1;
                            return acc;
                          }, {}) || {}
                        ).map(([category, count]) => ({ category, count }))
                      }>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="category" />
                        <YAxis />
                        <RechartsTooltip />
                        <Bar dataKey="count" fill="#8884d8" name="Risk Count" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Detailed Risk Factors */}
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Detailed Risk Factors
                    </Typography>
                    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                      {riskAssessment?.riskFactors?.map((risk: any) => (
                        <Box 
                          key={risk.id} 
                          sx={{ 
                            mb: 2, 
                            p: 2, 
                            bgcolor: 
                              risk.severity === 'critical' ? 'error.light' :
                              risk.severity === 'high' ? 'warning.light' :
                              risk.severity === 'medium' ? 'info.light' : 'success.light',
                            borderRadius: 1,
                            opacity: 0.8
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={risk.severity.toUpperCase()} 
                                size="small"
                                color={
                                  risk.severity === 'critical' ? 'error' :
                                  risk.severity === 'high' ? 'warning' :
                                  risk.severity === 'medium' ? 'info' : 'success'
                                }
                              />
                              <Chip label={risk.category} size="small" variant="outlined" />
                              <Typography variant="subtitle2">{risk.name}</Typography>
                            </Box>
                            <Typography variant="caption">Score: {risk.score}</Typography>
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {risk.description}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Affected: {risk.affectedEntity.type} - {risk.affectedEntity.name}
                          </Typography>
                          {risk.mitigationActions && risk.mitigationActions.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" fontWeight="bold">Mitigation Actions:</Typography>
                              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                                {risk.mitigationActions.map((action: string, idx: number) => (
                                  <li key={idx}>
                                    <Typography variant="caption">{action}</Typography>
                                  </li>
                                ))}
                              </ul>
                            </Box>
                          )}
                        </Box>
                      ))}
                      {(!riskAssessment?.riskFactors || riskAssessment.riskFactors.length === 0) && (
                        <Alert severity="success">
                          No risk factors detected. All systems operating normally.
                        </Alert>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Paper>
      </Box>
    </Layout>
  );
};

export default LeadershipDashboard;
